import {
    filter,
    map,
    reduce,
    take,
    values
} from 'ramda';
import { DxfImportDataForm, PipeDataDto } from 'providers/api';
import { i18n } from 'i18n';
import { Weaken } from 'utils';
import { createSpools, PREVIEW_SPOOL_COLOUR, SpoolEdge } from '../utils/nodeCreation';
import { NodeRow } from '../types';
import { NodeValidationResult } from '../utils/validation';
import { PCNode } from '../../MineModel/Nodes';
import pairKey from '../../MineModel/Spools/pairKey';
import PCSpool from '../../MineModel/Spools/PCSpool';
import { Model } from '../../hooks/useModelState';

// A file import produces a set of nodes and, separately, the edges between them. Edges used to be
// described by a `previousId` list on each node; they are spools now, so the importers carry them as
// their own list from the parser all the way to the payload.
export interface ImportGraph {
    nodes: PCNode[];
    edges: SpoolEdge[];
}

// An edge is identified by its unordered endpoint pair, so a pair stated twice - by both endpoints, or
// by two chains that share a segment - is one edge. The first statement wins: a later default pipe must
// never overwrite a pipe that an earlier, more specific mapping resolved.
export const dedupeEdges = (edges: SpoolEdge[]): SpoolEdge[] => Array.from(reduce(
    (acc: Map<string, SpoolEdge>, edge: SpoolEdge) => {
        if (!edge.nodeAId || !edge.nodeBId || edge.nodeAId === edge.nodeBId) return acc;
        const key = pairKey(edge.nodeAId, edge.nodeBId);
        return acc.has(key) ? acc : acc.set(key, edge);
    },
    new Map<string, SpoolEdge>(),
    edges,
).values());

// Renames chain (a node renamed to break a duplicate can then be merged into an existing node), so an
// id is followed to its final destination rather than substituted once.
const resolveId = (idMap: Map<string, string>, id: string): string => {
    const seen = new Set<string>([id]);
    let current = id;
    let next = idMap.get(current);
    while (next && !seen.has(next)) {
        seen.add(next);
        current = next;
        next = idMap.get(current);
    }
    return current;
};

// Validation drops a node when it duplicates an id or lands on an existing node's coordinates. The
// dropped node's edges have to be handed to the survivor: endpoints are remapped, edges that collapse
// onto a single node are discarded, and edges that collapse onto a pair that already has one are
// de-duplicated. Skipping this leaves edges pointing at ids that no longer exist - the server prunes
// them on load and the imported pipes silently are not there.
export const remapEdges = (edges: SpoolEdge[], idMap: Map<string, string>): SpoolEdge[] => dedupeEdges(
    map((edge: SpoolEdge) => ({
        ...edge,
        nodeAId: resolveId(idMap, edge.nodeAId),
        nodeBId: resolveId(idMap, edge.nodeBId),
    }), edges),
);

// `spoolsToRepoint` is validation's statement of which node was dropped in favour of which survivor.
export const idMapFromRepoints = (result: NodeValidationResult): Map<string, string> => reduce(
    (acc: Map<string, string>, repoint: { dropNodeId: string; keepNodeId: string }) => acc.set(repoint.dropNodeId, repoint.keepNodeId),
    new Map<string, string>(),
    result.spoolsToRepoint ?? [],
);

export function previewNodesToModel(
    graph: ImportGraph,
    model: Model,
    pipeData?: PipeDataDto,
): PCSpool[] {
    model.addNodes(graph.nodes);

    const nodeIndex = new Map<string, PCNode>(values(model.nodes).map((node) => [node.id, node]));
    graph.nodes.forEach((node) => nodeIndex.set(node.id, node));

    const spools = createSpools(graph.edges, model, { nodeIndex, pipeId: pipeData?.pipeId, spoolColour: PREVIEW_SPOOL_COLOUR });

    model.addSpools(spools);

    return spools;
}

// The DUF/DXF forms still describe the network the way the file does - a previous-node chain per node -
// and the server materialises the spools from it. Each edge is stated once, on whichever endpoint the
// payload actually carries, with its pipe as that endpoint's override so a per-line pipe mapping is not
// flattened to the node's default.
export function buildImportPayload(nodes: PCNode[], edges: SpoolEdge[]): DxfImportDataForm[] {
    const payloadIds = new Set<string>(nodes.map((node) => node.id));
    const previousByNode = new Map<string, string[]>();
    const overridesByNode = new Map<string, Record<string, string>>();

    dedupeEdges(edges).forEach((edge) => {
        const statedOn = payloadIds.has(edge.nodeBId) ? edge.nodeBId : edge.nodeAId;
        const other = statedOn === edge.nodeBId ? edge.nodeAId : edge.nodeBId;
        if (!payloadIds.has(statedOn)) return;

        previousByNode.set(statedOn, [...(previousByNode.get(statedOn) ?? []), other]);
        if (edge.pipeId) {
            overridesByNode.set(statedOn, { ...(overridesByNode.get(statedOn) ?? {}), [other]: edge.pipeId });
        }
    });

    return nodes.map((node) => ({
        nodeId: node.id,
        point: {
            x: node.coords.x,
            y: node.coords.y,
            z: node.coords.z,
        },
        previousNodeId: previousByNode.get(node.id) ?? [],
        pipeId: node.pipeId,
        // Neither file format carries a flow-loop length, and a node-level choke would be assigned by the
        // server to an incident spool of its own choosing - so nothing is stated rather than guessed.
        chokeLength: 0,
        pit: node.pit,
        isPit: !!node.pit,
        isStope: node.isStope ?? false,
        stopeData: undefined,
        level: node.level,
        area: node.area,
        isPlantNode: node.isPlantNode ?? false,
        ruptureDiskPressure: node.ruptureDiskPressure ?? 0,
        chokeHead: node.chokeHead ?? 0,
        spoolPipeOverrides: overridesByNode.get(node.id) ?? {},
    }));
}

export function removePreview(
    nodes: PCNode[],
    spools: PCSpool[],
    model: Model,
) {
    model.removeNodes(nodes);
    model.removeSpools(spools);
}

interface BoundingBox {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
    minZ: number;
    maxZ: number;
}

function calculateBoundingBox(currentNodes: PCNode[], expansionDistance: number): BoundingBox | null {
    if (currentNodes.length === 0) {
        return null;
    }

    const coords = map((node: PCNode) => ({
        x: node.coords.x,
        y: node.coords.y,
        z: node.coords.z,
    }), currentNodes);

    const boundingBox = reduce(
        (acc: BoundingBox, coord: { x: number; y: number; z: number }) => ({
            minX: Math.min(acc.minX, coord.x),
            maxX: Math.max(acc.maxX, coord.x),
            minY: Math.min(acc.minY, coord.y),
            maxY: Math.max(acc.maxY, coord.y),
            minZ: Math.min(acc.minZ, coord.z),
            maxZ: Math.max(acc.maxZ, coord.z),
        }),
        {
            minX: coords[0].x,
            maxX: coords[0].x,
            minY: coords[0].y,
            maxY: coords[0].y,
            minZ: coords[0].z,
            maxZ: coords[0].z,
        },
        coords,
    );

    return {
        minX: boundingBox.minX - expansionDistance,
        maxX: boundingBox.maxX + expansionDistance,
        minY: boundingBox.minY - expansionDistance,
        maxY: boundingBox.maxY + expansionDistance,
        minZ: boundingBox.minZ - expansionDistance,
        maxZ: boundingBox.maxZ + expansionDistance,
    };
}

const MAX_LISTED_NODE_IDS = 10;

// A file in the wrong units or coordinate system puts every node out of range, so the list is capped.
export const outsideImportDistanceMessage = (nodeIds: string[]): string => (nodeIds.length > MAX_LISTED_NODE_IDS
    ? i18n._(
        '{count} nodes are outside the allowed import distance, including:\n{nodeIds}',
        { count: nodeIds.length, nodeIds: take(MAX_LISTED_NODE_IDS, nodeIds).join(', ') },
    )
    : i18n._(
        'The following nodes are outside the allowed import distance:\n{nodeIds}',
        { nodeIds: nodeIds.join(', ') },
    ));

function checkDistanceToCenterGeneric<T>(
    model: Model,
    addedNodes: T[],
    getCoords: (node: T) => { x: number; y: number; z: number } | null,
    getNodeId: (node: T) => string | undefined,
): NodeValidationResult {
    const { nodes } = model;
    const expansionDistance = model.project.importDistance || 1000;

    const currentNodes = values(nodes);
    const expandedBounds = calculateBoundingBox(currentNodes, expansionDistance);

    if (!expandedBounds) {
        return {
            isValid: true,
            errors: [],
        };
    }

    const isWithinBounds = (node: T): boolean => {
        const coords = getCoords(node);
        if (!coords) {
            return false;
        }
        return coords.x >= expandedBounds.minX
            && coords.x <= expandedBounds.maxX
            && coords.y >= expandedBounds.minY
            && coords.y <= expandedBounds.maxY
            && coords.z >= expandedBounds.minZ
            && coords.z <= expandedBounds.maxZ;
    };

    const invalidNodes = filter((node: T) => !isWithinBounds(node), addedNodes);

    if (invalidNodes.length === 0) {
        return {
            isValid: true,
            errors: [],
        };
    }

    const invalidNodeIds = invalidNodes
        .map(getNodeId)
        .filter((id): id is string => !!id);

    const errors = invalidNodeIds.length > 0
        ? [outsideImportDistanceMessage(invalidNodeIds)]
        : [
            i18n._(
                'Some nodes are outside the allowed import distance',
                {},
                { message: 'Some nodes are outside the allowed import distance' },
            ),
        ];

    return {
        isValid: false,
        errors,
    };
}

export function checkDistanceToCenter(model: Model, addedNodes: Weaken<NodeRow>[]): NodeValidationResult {
    return checkDistanceToCenterGeneric(
        model,
        addedNodes,
        (node) => {
            if (node.x === undefined || node.y === undefined || node.z === undefined) {
                return null;
            }
            return { x: node.x, y: node.y, z: node.z };
        },
        (node) => node.nodeId,
    );
}

export function checkDistanceToCenterForPCNodes(model: Model, addedNodes: PCNode[]): NodeValidationResult {
    return checkDistanceToCenterGeneric(
        model,
        addedNodes,
        (node) => ({ x: node.coords.x, y: node.coords.y, z: node.coords.z }),
        (node) => node.id,
    );
}

interface Coords {
    x: number;
    y: number;
    z: number;
}

const pointIsOnLineSegment = (
    pointToCheck: Coords,
    segmentStart: Coords,
    segmentEnd: Coords,
    tolerance: number,
): boolean => {
    const isAtSegmentStart = (Math.abs(pointToCheck.x - segmentStart.x) < tolerance
        && Math.abs(pointToCheck.y - segmentStart.y) < tolerance
        && Math.abs(pointToCheck.z - segmentStart.z) < tolerance);
    const isAtSegmentEnd = (Math.abs(pointToCheck.x - segmentEnd.x) < tolerance
        && Math.abs(pointToCheck.y - segmentEnd.y) < tolerance
        && Math.abs(pointToCheck.z - segmentEnd.z) < tolerance);

    if (isAtSegmentStart || isAtSegmentEnd) {
        return false;
    }

    const vectorAPx = pointToCheck.x - segmentStart.x;
    const vectorAPy = pointToCheck.y - segmentStart.y;
    const vectorAPz = pointToCheck.z - segmentStart.z;

    const vectorABx = segmentEnd.x - segmentStart.x;
    const vectorABy = segmentEnd.y - segmentStart.y;
    const vectorABz = segmentEnd.z - segmentStart.z;

    const segmentMagnitudeSq = vectorABx ** 2 + vectorABy ** 2 + vectorABz ** 2;

    if (segmentMagnitudeSq < tolerance ** 2) {
        return false;
    }

    const dotProduct = vectorAPx * vectorABx + vectorAPy * vectorABy + vectorAPz * vectorABz;

    if (dotProduct < 0 || dotProduct > segmentMagnitudeSq) {
        return false;
    }

    const crossProductX = vectorAPy * vectorABz - vectorAPz * vectorABy;
    const crossProductY = vectorAPz * vectorABx - vectorAPx * vectorABz;
    const crossProductZ = vectorAPx * vectorABy - vectorAPy * vectorABx;
    const crossProductMagnitudeSq = crossProductX ** 2 + crossProductY ** 2 + crossProductZ ** 2;

    const distanceSq = crossProductMagnitudeSq / segmentMagnitudeSq;

    return distanceSq < tolerance ** 2;
};

interface SegmentRef {
    key: string;
    n1: PCNode;
    n2: PCNode;
    minX: number; minY: number; minZ: number;
    maxX: number; maxY: number; maxZ: number;
}

class SegmentGrid {
    private cellSize: number;

    private cells: Map<string, SegmentRef[]>;

    constructor(cellSize: number) {
        this.cellSize = cellSize;
        this.cells = new Map<string, SegmentRef[]>();
    }

    insert(seg: SegmentRef): void {
        const cs = this.cellSize;
        const x0 = Math.floor(seg.minX / cs);
        const x1 = Math.floor(seg.maxX / cs);
        const y0 = Math.floor(seg.minY / cs);
        const y1 = Math.floor(seg.maxY / cs);
        const z0 = Math.floor(seg.minZ / cs);
        const z1 = Math.floor(seg.maxZ / cs);
        for (let cx = x0; cx <= x1; cx += 1) {
            for (let cy = y0; cy <= y1; cy += 1) {
                for (let cz = z0; cz <= z1; cz += 1) {
                    const k = `${cx},${cy},${cz}`;
                    const bucket = this.cells.get(k);
                    if (bucket) {
                        bucket.push(seg);
                    } else {
                        this.cells.set(k, [seg]);
                    }
                }
            }
        }
    }

    queryPoint(px: number, py: number, pz: number, margin: number): SegmentRef[] {
        const cs = this.cellSize;
        const x0 = Math.floor((px - margin) / cs);
        const x1 = Math.floor((px + margin) / cs);
        const y0 = Math.floor((py - margin) / cs);
        const y1 = Math.floor((py + margin) / cs);
        const z0 = Math.floor((pz - margin) / cs);
        const z1 = Math.floor((pz + margin) / cs);
        const seen = new Set<SegmentRef>();
        const result: SegmentRef[] = [];
        for (let cx = x0; cx <= x1; cx += 1) {
            for (let cy = y0; cy <= y1; cy += 1) {
                for (let cz = z0; cz <= z1; cz += 1) {
                    const bucket = this.cells.get(`${cx},${cy},${cz}`);
                    if (bucket) {
                        bucket.forEach((s) => {
                            if (!seen.has(s)) {
                                seen.add(s);
                                result.push(s);
                            }
                        });
                    }
                }
            }
        }
        return result;
    }
}

const buildSegments = (edges: SpoolEdge[], nodeById: Map<string, PCNode>, tolerance: number): SegmentRef[] => reduce(
    (acc: SegmentRef[], edge: SpoolEdge) => {
        const n1 = nodeById.get(edge.nodeAId);
        const n2 = nodeById.get(edge.nodeBId);
        if (!n1 || !n2) return acc;

        const { x: ax, y: ay, z: az } = n1.coords;
        const { x: bx, y: by, z: bz } = n2.coords;
        return [...acc, {
            key: pairKey(n1.id, n2.id),
            n1,
            n2,
            minX: Math.min(ax, bx) - tolerance,
            minY: Math.min(ay, by) - tolerance,
            minZ: Math.min(az, bz) - tolerance,
            maxX: Math.max(ax, bx) + tolerance,
            maxY: Math.max(ay, by) + tolerance,
            maxZ: Math.max(az, bz) + tolerance,
        }];
    },
    [],
    edges,
);

const buildAdjacency = (edges: SpoolEdge[]): Map<string, Set<string>> => reduce(
    (acc: Map<string, Set<string>>, edge: SpoolEdge) => {
        const a = acc.get(edge.nodeAId) ?? new Set<string>();
        const b = acc.get(edge.nodeBId) ?? new Set<string>();
        a.add(edge.nodeBId);
        b.add(edge.nodeAId);
        return acc.set(edge.nodeAId, a).set(edge.nodeBId, b);
    },
    new Map<string, Set<string>>(),
    edges,
);

// A node lying on an existing edge splits it. The edge is replaced by the chain through the nodes that
// landed on it; every segment of the chain inherits the original edge's pipe, and the flow-loop length
// stays on the first segment rather than being repeated onto each one.
const applySplits = (
    edges: SpoolEdge[],
    splitsPerEdge: Map<string, PCNode[]>,
    nodeById: Map<string, PCNode>,
): SpoolEdge[] => dedupeEdges(edges.reduce<SpoolEdge[]>((acc, edge) => {
    const splitNodes = splitsPerEdge.get(pairKey(edge.nodeAId, edge.nodeBId));
    const nodeA = nodeById.get(edge.nodeAId);
    const nodeB = nodeById.get(edge.nodeBId);
    if (!splitNodes || splitNodes.length === 0 || !nodeA || !nodeB) return [...acc, edge];

    const distanceFromA = (node: PCNode) => (node.coords.x - nodeA.coords.x) ** 2
        + (node.coords.y - nodeA.coords.y) ** 2
        + (node.coords.z - nodeA.coords.z) ** 2;

    const chain = [nodeA, ...[...splitNodes].sort((p1, p2) => distanceFromA(p1) - distanceFromA(p2)), nodeB];

    return [...acc, ...chain.slice(0, -1).map((node, index) => ({
        nodeAId: node.id,
        nodeBId: chain[index + 1].id,
        pipeId: edge.pipeId,
        chokeLength: index === 0 ? edge.chokeLength : 0,
    }))];
}, []));

// Imported lines cross each other at points that are not vertices of both lines. Every node that sits on
// another line's segment has to split that segment, or the two lines look joined on screen and are not
// connected in the graph.
export const resolveIntersectionsAndSplitLines = (
    graph: ImportGraph,
    tolerance: number = 0.5,
): ImportGraph => {
    const MAX_ITERATIONS = 30;
    const nodeById = new Map<string, PCNode>(graph.nodes.map((node) => [node.id, node]));

    let edges = dedupeEdges(graph.edges);
    let iteration = 0;
    let changesMade = true;

    while (changesMade && iteration < MAX_ITERATIONS) {
        changesMade = false;
        iteration += 1;

        const segments = buildSegments(edges, nodeById, tolerance);
        if (segments.length === 0) break;

        const bounds = reduce(
            (acc: number[], seg: SegmentRef) => [
                Math.min(acc[0], seg.minX), Math.min(acc[1], seg.minY), Math.min(acc[2], seg.minZ),
                Math.max(acc[3], seg.maxX), Math.max(acc[4], seg.maxY), Math.max(acc[5], seg.maxZ),
            ],
            [Infinity, Infinity, Infinity, -Infinity, -Infinity, -Infinity],
            segments,
        );

        const maxExtent = Math.max(bounds[3] - bounds[0], bounds[4] - bounds[1], bounds[5] - bounds[2], 1);
        const cellSize = Math.max(maxExtent / Math.sqrt(Math.max(1, segments.length)), tolerance * 10);

        const grid = new SegmentGrid(cellSize);
        segments.forEach((seg) => grid.insert(seg));

        const adjacency = buildAdjacency(edges);
        const splitsPerEdge = new Map<string, PCNode[]>();

        graph.nodes.forEach((node) => {
            const neighbours = adjacency.get(node.id);
            grid.queryPoint(node.coords.x, node.coords.y, node.coords.z, tolerance).forEach((seg) => {
                if (node.id === seg.n1.id || node.id === seg.n2.id) return;
                if (neighbours?.has(seg.n1.id) && neighbours?.has(seg.n2.id)) return;
                if (!pointIsOnLineSegment(node.coords, seg.n1.coords, seg.n2.coords, tolerance)) return;

                splitsPerEdge.set(seg.key, [...(splitsPerEdge.get(seg.key) ?? []), node]);
            });
        });

        if (splitsPerEdge.size === 0) break;

        changesMade = true;
        edges = applySplits(edges, splitsPerEdge, nodeById);
    }

    return { nodes: graph.nodes, edges };
};
